/** Separate product sessions; never borrow the other site's administrator token. */
import { safeLocalStorage, safeSessionStorage, assertSessionStorage, sessionStorageError } from './safeStorage';
export const HOME_SMART_AREA = '/home-smart/area';
export const HOME_SMART_LOGIN = '/home-smart/accedi';
const raw = () => safeLocalStorage;
const TAB_SITE = 'active_site';
const external = value => { try { return JSON.parse(value || 'null')?.is_external_tenant === true; } catch { return false; } };
export const isInternalImpersonation = () => raw().getItem('hw_impersonating') === 'true';
export function isHomeSmartSite() {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname;
  if (path === '/home-smart' || path.startsWith('/home-smart/')) return true;
  const params = new URLSearchParams(window.location.search);
  return params.has('wallet') || params.has('white_label') || safeSessionStorage.getItem(TAB_SITE) === 'home-smart' ||
    (!isInternalImpersonation() && external(raw().getItem('hw_user')));
}
export const currentSite = () => isHomeSmartSite() ? 'home-smart' : 'hw-smart';
const keyFor = (key, site) => site === 'home-smart' && key.startsWith('hw_') ? `home_smart_${key.slice(3)}` : key;
export const tokenForSite = site => raw().getItem(keyFor('hw_token', site));
export function migrateLegacyExternalSession() {
  if (isInternalImpersonation() || !external(raw().getItem('hw_user')) || !raw().getItem('hw_token')) return;
  if (!raw().getItem('home_smart_token')) {
    try { saveSiteSession(raw().getItem('hw_token'), JSON.parse(raw().getItem('hw_user')), 'home-smart'); }
    catch { return; } // Keep the source session intact if persistence is blocked.
  }
  raw().removeItem('hw_token'); raw().removeItem('hw_user'); raw().removeItem('home_smart_context');
  safeSessionStorage.setItem(TAB_SITE, 'home-smart');
}
export function saveSiteSession(token, user, site = user?.is_external_tenant ? 'home-smart' : 'hw-smart') {
  if (!token || !user || (site === 'home-smart' && user.is_external_tenant !== true)) throw new Error('Accesso non disponibile. Verifica le credenziali.');
  assertSessionStorage();
  const tokenKey = keyFor('hw_token', site), userKey = keyFor('hw_user', site);
  const oldToken = raw().getItem(tokenKey), oldUser = raw().getItem(userKey), serialized = JSON.stringify(user);
  if (!raw().setItem(tokenKey, token) || !raw().setItem(userKey, serialized) || raw().getItem(tokenKey) !== token || raw().getItem(userKey) !== serialized) {
    [[tokenKey, oldToken], [userKey, oldUser]].forEach(([key, value]) => value === null ? raw().removeItem(key) : raw().setItem(key, value));
    throw sessionStorageError();
  }
  safeSessionStorage.setItem(TAB_SITE, site);
}
export function clearSiteSession(site = currentSite(), expectedToken) {
  if (expectedToken !== undefined && tokenForSite(site) !== expectedToken) return false;
  raw().removeItem(keyFor('hw_token', site)); raw().removeItem(keyFor('hw_user', site));
  return true;
}
// Explicit compatibility import in legacy consumers; no global monkeypatch.
export const brandStorage = {
  getItem(key) {
    if (key === 'home_smart_context') return isHomeSmartSite() ? 'true' : null;
    return raw().getItem(keyFor(key, currentSite()));
  },
  setItem(key, value) {
    if (key === 'home_smart_context') {
      if (isHomeSmartSite() && value === 'true') safeSessionStorage.setItem(TAB_SITE, 'home-smart');
    } else raw().setItem(keyFor(key, currentSite()), value);
  },
  removeItem(key) { if (key !== 'home_smart_context') raw().removeItem(keyFor(key, currentSite())); },
  clear() {
    const prefix = isHomeSmartSite() ? 'home_smart_' : 'hw_';
    raw().keys().filter(key => key.startsWith(prefix)).forEach(key => raw().removeItem(key));
  },
};
export function homeSmartAppPath(path = '/responsabile/dashboard') {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//') || path.includes('\\')) path = '/responsabile/dashboard';
  return path === HOME_SMART_AREA || path.startsWith(`${HOME_SMART_AREA}/`) ? path : HOME_SMART_AREA + path;
}
export function homeSmartEntryPath(path) {
  const fallback = homeSmartAppPath('/responsabile/dashboard');
  if (typeof path !== 'string' || !/^\/responsabile(?:\/|\?|#|$)/.test(path)) return fallback;
  // Redirects must be canonical paths, never encoded separators or dot segments.
  const pathname = path.split(/[?#]/, 1)[0];
  if (/[\\%\s\u0000-\u001f\u007f]/.test(pathname) || pathname.split('/').some(part => part === '.' || part === '..')) return fallback;
  return homeSmartAppPath(path);
}
export function isHomeSmartPublicPage() {
  const path = window.location.pathname;
  return (path.startsWith('/home-smart') && !path.startsWith(HOME_SMART_AREA)) || path.startsWith(`${HOME_SMART_AREA}/home-smart/`);
}
export function canonicalHomeSmartPath() {
  if (!isHomeSmartSite()) return null;
  const {pathname, search, hash} = window.location;
  if (pathname.startsWith(`${HOME_SMART_AREA}/home-smart/`)) return pathname.slice(HOME_SMART_AREA.length) + search + hash;
  const relative = pathname.startsWith(HOME_SMART_AREA) ? pathname.slice(HOME_SMART_AREA.length) : pathname;
  const publicPages = {'/login': HOME_SMART_LOGIN, '/reset-password': '/home-smart/reimposta-password',
    '/privacy': '/home-smart/privacy', '/privacy-policy': '/home-smart/privacy', '/terms': '/home-smart/condizioni', '/dpa': '/home-smart/dpa'};
  if (publicPages[relative]) return publicPages[relative] + search;
  if (pathname.startsWith('/home-smart')) return null;
  return homeSmartAppPath(pathname + search + hash);
}